import axios from 'axios';
import { LoginRequest, RegisterRequest, AuthResponse, User } from '@/types/user';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Request interceptor to add auth token and fix URL paths
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Fix URL: if baseURL is set and url starts with '/', remove the leading slash
    // This ensures the baseURL is properly combined with the path
    if (config.baseURL && config.url && config.url.startsWith('/')) {
      config.url = config.url.substring(1);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          }, {
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0',
            },
          });

          const { token } = response.data.data;
          localStorage.setItem('token', token);

          // Retry the original request
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens but don't redirect
        // Let the ProtectedRoute component handle the redirect
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (data: LoginRequest): Promise<AuthResponse['data']> => {
    const response = await api.post('/auth/login', data);
    return response.data.data;
  },

  register: async (data: RegisterRequest): Promise<AuthResponse['data']> => {
    const response = await api.post('/auth/register', data);
    return response.data.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  getProfile: async (): Promise<User> => {
    try {
      const response = await api.get('/auth/profile');
      const userData = response.data.data;
      
      // Transform snake_case to camelCase to match frontend types
      // Handle agent_profiles which can be null (one-to-one relationship)
      const agentProfile = userData.agent_profiles || userData.agentProfile || null;
      const studentProfile = userData.student_profiles || userData.studentProfile || null;
      const adminProfile = userData.admin_profiles || userData.adminProfile || null;
      
      const transformedUser: User = {
        ...userData,
        studentProfile: studentProfile,
        agentProfile: agentProfile,
        adminProfile: adminProfile,
      };
      
      // Remove snake_case properties if they exist
      delete (transformedUser as any).student_profiles;
      delete (transformedUser as any).agent_profiles;
      delete (transformedUser as any).admin_profiles;
      
      return transformedUser;
    } catch (error: any) {
      console.error('getProfile error:', error);
      // If it's a 401, the token is invalid - let the error propagate
      if (error.response?.status === 401) {
        throw error;
      }
      throw new Error(error.response?.data?.error || error.message || 'Failed to get profile');
    }
  },

  forgotPassword: async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
  },

  resetPassword: async (token: string, password: string): Promise<void> => {
    await api.post('/auth/reset-password', { token, password });
  },

  verifyEmail: async (token: string): Promise<void> => {
    await api.post('/auth/verify-email', { token });
  },
};
